import { useStylesHook } from "../hooks/useStylesHook";
import { getColorOptions } from "../theming/colorOptions";
import { SCREEN_READER_ONLY } from "../theming/helpers";
import { PORTAL_THEME, TMS_THEME } from "../theming/theming";
const COMPONENT_BORDER_RADIUS = 4;
const BASE_STATUS_LABEL_STYLES = {
    borderRadius: COMPONENT_BORDER_RADIUS,
    textTransform: "uppercase",
    fontWeight: 700,
    "-webkit-font-smoothing": "antialiased",
    letterSpacing: 0.5,
    whiteSpace: "nowrap",
};
export const useStatusLabelClasses = (size, prependAppend, color) => useStylesHook({
    statusLabel: {
        ...BASE_STATUS_LABEL_STYLES,
        color: getColorOptions({ color }),
        backgroundColor: getColorOptions({ color, contrast: true }),
        ...getSizeBasedStyles(prependAppend, size),
    },
});
const getSizeBasedStyles = (prependAppend, size) => {
    switch (size) {
        case "xs":
            return getXSStatusLabelStyles(prependAppend);
        case "small":
            return getSmallStatusLabelStyles(prependAppend);
        case "medium":
        default:
            return getMediumStatusLabelStyles(prependAppend);
    }
};
const BASE_XS_STATUS_LABEL_STYLES = {
    fontSize: 10,
    padding: [2, 4, 2],
    height: 16,
    paddingLeft: 4,
    paddingRight: 4,
    paddingTop: 2,
    paddingBottom: 2,
};
export const getXSStatusLabelStyles = (prependAppend) => prependAppend
    ? { ...BASE_XS_STATUS_LABEL_STYLES, paddingLeft: 0, paddingRight: 0 }
    : BASE_XS_STATUS_LABEL_STYLES;
const BASE_SM_STATUS_LABEL_STYLES = {
    fontSize: PORTAL_THEME.typography.statusLabel.fontSize ?? 11,
    padding: [2, 4, 1.5],
    height: 17.5,
};
export const getSmallStatusLabelStyles = (prependAppend) => prependAppend
    ? { ...BASE_SM_STATUS_LABEL_STYLES, padding: [1.5, 0, 3, 0] }
    : BASE_SM_STATUS_LABEL_STYLES;
const BASE_MD_STATUS_LABEL_STYLES = { padding: [3, 6], fontSize: 12 };
export const getMediumStatusLabelStyles = (prependAppend) => prependAppend
    ? { ...BASE_MD_STATUS_LABEL_STYLES, padding: [3, 0, 3, 0], height: 21 }
    : BASE_MD_STATUS_LABEL_STYLES;
const STATIC_PREPEND_STYLES = {
    borderTopLeftRadius: COMPONENT_BORDER_RADIUS,
    borderBottomLeftRadius: COMPONENT_BORDER_RADIUS,
    color: TMS_THEME.palette.gray.white.color,
};
const STATIC_APPEND_STYLES = {
    borderTopRightRadius: COMPONENT_BORDER_RADIUS,
    borderBottomRightRadius: COMPONENT_BORDER_RADIUS,
    color: TMS_THEME.palette.gray.white.color,
};
const getPrependAppendSizeStyles = (size) => {
    switch (size) {
        case "xs":
            return [2, 3.5, 2];
        case "small":
            return [1.5, 3.5, 3];
        case "medium":
        default:
            return [3, 3.5];
    }
};
export const usePrependAppendStatusLabelClasses = (size, color) => useStylesHook({
    prepend: {
        backgroundColor: getColorOptions({ color }),
        padding: getPrependAppendSizeStyles(size),
        ...STATIC_PREPEND_STYLES,
    },
    append: {
        backgroundColor: getColorOptions({ color }),
        padding: getPrependAppendSizeStyles(size),
        ...STATIC_APPEND_STYLES,
    },
});
export const useStatusLabelBodyClasses = (hasIcon, prependAppend) => useStylesHook({
    additionalText: {
        padding: hasIcon ? [0, 6] : [0, 4, 0, prependAppend ? 4 : 6],
    },
});
const STATIC_MESSAGE_STYLES = { flex: 1 };
const STATIC_ICON_STYLES = { position: "absolute" };
const ICON_STYLES = {
    small: {
        glyphicon: {
            icon: {
                paddingRight: 8,
                width: 12,
                top: 2,
            },
            message: {
                paddingLeft: 16,
                paddingRight: 0,
                lineHeight: 1.1,
                paddingTop: 1,
            },
        },
        "glyphicon-pro": {
            icon: {
                paddingRight: 0,
                fontSize: 15,
                marginLeft: 0,
                width: 18,
                top: -1,
            },
            message: {
                lineHeight: 1,
                paddingTop: 0,
                paddingLeft: 18,
                paddingRight: 0,
            },
        },
    },
    medium: {
        glyphicon: {
            icon: {
                paddingRight: 8,
                width: 16,
            },
            message: {
                lineHeight: 1,
                paddingTop: 0,
                paddingLeft: 16,
                paddingRight: 0,
            },
        },
        "glyphicon-pro": {
            icon: {
                paddingRight: 0,
                width: 24,
                top: -3,
                fontSize: 18,
                marginLeft: -3,
            },
            message: {
                lineHeight: 1,
                paddingTop: 0,
                paddingLeft: 18,
                paddingRight: 0,
            },
        },
    },
    xs: {
        glyphicon: {
            icon: {
                paddingRight: 8,
                width: 16,
            },
            message: {
                lineHeight: 1,
                paddingTop: 0,
                paddingLeft: 16,
                paddingRight: 0,
            },
        },
        "glyphicon-pro": {
            icon: {
                paddingRight: 0,
                width: 16,
                top: -1,
                fontSize: 16,
                marginLeft: -3,
            },
            message: {
                lineHeight: 1,
                paddingTop: 0,
                paddingLeft: 16,
                paddingRight: 0,
            },
        },
    },
};
export const useIconWithTextClasses = (iconSize, iconType) => useStylesHook({
    wrapper: {
        height: 14,
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-start",
        flexDirection: "row",
    },
    srOnly: SCREEN_READER_ONLY,
    message: {
        ...STATIC_MESSAGE_STYLES,
        ...(iconType && ICON_STYLES[iconSize][iconType].message),
    },
    icon: {
        ...STATIC_ICON_STYLES,
        ...(iconType && ICON_STYLES[iconSize][iconType].icon),
    },
});
